/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../../../../../core/components';
import { CertModel, CertsApiService, LbErrorService } from '../../../../../shared';
import { Component, OnInit } from '@angular/core';
import { ErrorResponseModel } from '../../../../../../../core/models';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LogService, ModalService, UtilsService } from '../../../../../../../core/services';
import { Observable } from 'rxjs';
import { nameValidation } from '../../../../../../../core/directives';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-cert',
    templateUrl: './addEditCert.component.html',
    styleUrls: ['./addEditCert.component.scss']
})
export class AddEditCertComponent extends BaseComponent implements OnInit {
    /**
     * Store page title.
     */
    public pageTitle: string;

    /**
     * Store the cert id.
     */
    public id: string;

    /**
     * Embedded in wizard flag.
     */
    public componentEmbedded: boolean = false;

    /**
     * Store the callback function passed in from parent component.
     */
    public callBack: (...args: any[]) => void;

    /**
     * Store the page type when embedded in the wizard.
     */
    public embeddedPageType: string;

    /**
     * Show CRL form.
     */
    public showCRLForm: boolean = false;

    /**
     * Store the specific form for the page.
     */
    public form: FormGroup;

    /**
     * Store initial form data.
     * @private
     */
    private initialFormData: any = {};

    /**
     * Store the url path,
     * @private
     */
    private path: string;

    constructor(
        private certsApiService: CertsApiService,
        private formBuilder: FormBuilder,
        private modalService: ModalService,
        private lbErrorService: LbErrorService,
        private logService: LogService,
        private route: ActivatedRoute,
        private router: Router,
        private utilsService: UtilsService
    ) {
        super();
    }

    public ngOnInit(): void {
        this.id = this.componentEmbedded ? this.id : this.route.snapshot.params['id'];
        this.path = this.route.snapshot.url[0].path;
        this.setPageTitle();
        this.setUpForms();
        this.setUpFormSubscriber();
        if (this.id) {
            this.setUpPageData();
        }
    }

    /**
     * Submit certificate.
     */
    public submitCertificate(force?: boolean): Observable<any> {
        return new Observable<any>((observer) => {
            const apiCall: string = this.getApiCall();
            const cert: any = this.form.value;
            if (force) {
                cert['force'] = true;
            }
            this.certsApiService[apiCall](cert)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.returnToParent();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.checkCertExpired(error);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Check if certificate has expired and display force upload modal.
     * @private
     * @param apiError
     */
    private checkCertExpired(apiError: ErrorResponseModel): void {
        let certExpired: boolean = false;
        for (const error of apiError.error?.errors) {
            if (error.message?.includes('expired')) {
                certExpired = true;
                break;
            }
        }

        if (certExpired) {
            this.modalService.openModalInParent('force-upload-cert');
        } else {
            this.lbErrorService.pageWithErrors(this.form, apiError.error.errors);
            this.modalService.close('force-upload-cert');
        }
    }

    /**
     * Return to parent page.
     */
    public returnToParent(): void {
        if (this.componentEmbedded) {
            this.callBack();
        } else {
            const url: string = this.id ? '../../../' : '../../';
            this.router.navigate([url], { relativeTo: this.route }).then().catch();
        }
    }

    /**
     * Cancel upload
     */
    public closeUploadModal(): void {
        this.modalService.close('force-upload-cert');
    }

    /**
     * Get relevant api call.
     * @private
     */
    private getApiCall(): string {
        let apiCall: string;
        if (this.path === 'crl' || this.embeddedPageType === 'crl') {
            apiCall = this.id ? 'updateCRL' : 'createCRL';
        } else if (this.path === 'ca' || this.embeddedPageType === 'ca') {
            apiCall = this.id ? 'updateCA' : 'createCA';
        } else {
            apiCall = this.id ? 'updateCert' : 'addCert';
        }

        return apiCall;
    }

    /**
     * Set page title
     * @private
     */
    private setPageTitle(): void {
        if (this.path === 'crl' || this.embeddedPageType === 'crl') {
            this.pageTitle = this.id ? 'certificate_edit_crl' : 'certificate_add_crl';
            this.showCRLForm = true;
        } else if (this.path === 'ca' || this.embeddedPageType === 'ca') {
            this.pageTitle = this.id ? 'certificate_edit_ca' : 'certificate_add_ca';
        } else {
            this.pageTitle = this.id ? 'certificate_edit_certificate' : 'certificate_add_certificate';
        }
    }

    /**
     * Set up page data on edit.
     * @private
     */
    private setUpPageData(): void {
        const apiCall: string = this.getApiCallForSetup();
        this.certsApiService[apiCall](this.id)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (cert: CertModel): void => {
                    this.patchToForm(cert);
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error(
                        'AddEditCertComponent: setUpPageData', 'Failed to fetch data', error);
                }
            });
    }

    /**
     * Get api call to set up the page.
     * @private
     */
    private getApiCallForSetup(): string {
        let apiCall: string;

        if (this.path === 'crl' || this.embeddedPageType === 'crl') {
            apiCall = 'getCRL';
        } else if (this.path === 'ca' || this.embeddedPageType === 'ca') {
            apiCall = 'getCA';
        } else {
            apiCall = 'getCert';
        }

        return apiCall;
    }

    /**
     * Set up the certificates form.
     * @private
     */
    private setUpForms(): void {
        if (this.path === 'crl' || this.embeddedPageType === 'crl') {
            this.form = this.formBuilder.group({
                id: [''],
                name: [null, [Validators.required, nameValidation]],
                crl: [null, [Validators.required]],
            });
        } else {
            this.form = this.formBuilder.group({
                id: [''],
                name: [null, [Validators.required, nameValidation]],
                privateKey: [null, [Validators.required]],
                cert: [null, [Validators.required]],
            });
        }
    }

    /**
     * Patch data to form.
     * @param cert
     * @private
     */
    private patchToForm(cert: CertModel): void {
        this.form.patchValue(cert);
        this.form.get('id').setValue(this.id);
        this.initialFormData = this.form.value;
    }

    /**
     * Set up form subscribers.
     * @private
     */
    private setUpFormSubscriber(): void {
        this.form.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (formValues: any): void => {
                    this.utilsService.debounce('formValues', (): void => {
                        if (this.id) {
                            if (!this.utilsService.hasFormDataChanged(this.initialFormData, formValues)) {
                                this.form.markAsPristine();
                            } else {
                                this.form.markAsDirty();
                            }
                        }
                    }, 250);
                }
            });
    }
}
